import { useState, useEffect } from 'react';
import { Loader2, Save, Send, CheckCircle2, AlertTriangle, Plus, Pencil, Trash2, Star, FolderOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/core/apiClient';

interface BankAccount {
  id: string;
  iban: string;
  bankName: string | null;
  accountHolder: string | null;
  isDefault: number;
}

interface BankAccountForm {
  iban: string;
  bankName: string;
  accountHolder: string;
  isDefault: boolean;
}

const emptyBankForm: BankAccountForm = { iban: '', bankName: '', accountHolder: '', isDefault: false };

interface SmtpConfig {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  from: string;
  hasPassword: boolean;
  source: 'database' | 'env';
}

interface FormData {
  host: string;
  port: string;
  secure: boolean;
  user: string;
  pass: string;
  from: string;
}

const initialFormData: FormData = {
  host: '',
  port: '587',
  secure: false,
  user: '',
  pass: '',
  from: '',
};

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [hasPassword, setHasPassword] = useState(false);
  const [source, setSource] = useState<'database' | 'env'>('env');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Dropbox state
  const [dropboxKeyInput, setDropboxKeyInput] = useState('');
  const [dropboxSaving, setDropboxSaving] = useState(false);
  const [dropboxSuccess, setDropboxSuccess] = useState<string | null>(null);
  const [dropboxError, setDropboxError] = useState<string | null>(null);

  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [bankForm, setBankForm] = useState<BankAccountForm>(emptyBankForm);
  const [editingBankId, setEditingBankId] = useState<string | null>(null);
  const [showBankForm, setShowBankForm] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);

  useEffect(() => {
    loadSmtpConfig();
    loadBankAccounts();
    loadDropboxConfig();
  }, []);

  const loadDropboxConfig = async () => {
    try {
      const res = await api.get<{ success: boolean; data: { appKey: string } }>('/admin/settings/dropbox');
      setDropboxKeyInput(res.data?.appKey || '');
    } catch { /* silent */ }
  };

  const handleDropboxSave = async () => {
    setDropboxSaving(true);
    setDropboxError(null);
    setDropboxSuccess(null);
    try {
      await api.put('/admin/settings/dropbox', { appKey: dropboxKeyInput });
      setDropboxSuccess('App Key de Dropbox guardada correctament');
    } catch (err: any) {
      setDropboxError(err?.response?.data?.message || 'Error en guardar la configuració de Dropbox');
    } finally {
      setDropboxSaving(false);
    }
  };

  const loadSmtpConfig = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: SmtpConfig }>('/admin/settings/smtp');
      const c = res.data;
      setFormData({
        host: c.host || '',
        port: String(c.port || 587),
        secure: c.secure,
        user: c.user || '',
        pass: '',
        from: c.from || '',
      });
      setHasPassword(c.hasPassword);
      setSource(c.source);
    } catch (err) {
      console.error('[AdminSettings] Error carregant configuració SMTP:', err);
      setError('Error en carregar la configuració SMTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await api.put<{ success: boolean; data: SmtpConfig; message: string }>('/admin/settings/smtp', {
        host: formData.host,
        port: formData.port,
        secure: String(formData.secure),
        user: formData.user,
        pass: formData.pass || undefined,
        from: formData.from,
      });
      setSuccess(res.message || 'Configuració desada correctament');
      setHasPassword(res.data.hasPassword);
      setSource(res.data.source);
      setFormData((p) => ({ ...p, pass: '' }));
    } catch (err: any) {
      console.error('[AdminSettings] Error guardando configuración SMTP:', err);
      setError(err.response?.data?.message || 'Error en desar la configuració');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) return;
    setSendingTest(true);
    setTestResult(null);
    try {
      const res = await api.post<{ success: boolean; message: string }>('/admin/settings/smtp/test', { to: testEmail });
      setTestResult({ ok: true, message: res.message || 'Correu de prova enviat' });
    } catch (err: any) {
      setTestResult({ ok: false, message: err.response?.data?.message || 'Error en enviar el correu de prova' });
    } finally {
      setSendingTest(false);
    }
  };

  const loadBankAccounts = async () => {
    try {
      const res = await api.get<{ success: boolean; data: BankAccount[] }>('/admin/settings/bank-accounts');
      setBankAccounts(res.data || []);
    } catch {
      setBankAccounts([]);
    }
  };

  const handleBankEdit = (account: BankAccount) => {
    setEditingBankId(account.id);
    setBankForm({
      iban: account.iban,
      bankName: account.bankName || '',
      accountHolder: account.accountHolder || '',
      isDefault: account.isDefault === 1,
    });
    setShowBankForm(true);
    setBankError(null);
  };

  const handleBankCancel = () => {
    setShowBankForm(false);
    setEditingBankId(null);
    setBankForm(emptyBankForm);
    setBankError(null);
  };

  const handleBankSave = async () => {
    if (!bankForm.iban.trim()) { setBankError('El camp IBAN és obligatori'); return; }
    setSavingBank(true);
    setBankError(null);
    try {
      if (editingBankId) {
        await api.put(`/admin/settings/bank-accounts/${editingBankId}`, bankForm);
      } else {
        await api.post('/admin/settings/bank-accounts', bankForm);
      }
      await loadBankAccounts();
      handleBankCancel();
    } catch (err: any) {
      setBankError(err?.response?.data?.message || 'Error en desar el compte bancari');
    } finally {
      setSavingBank(false);
    }
  };

  const handleBankDelete = async (id: string) => {
    if (!confirm('Segur que vols eliminar aquest compte bancari?')) return;
    try {
      await api.delete(`/admin/settings/bank-accounts/${id}`);
      await loadBankAccounts();
    } catch {
      // silent
    }
  };

  const formatIban = (iban: string) => iban.replace(/(.{4})/g, '$1 ').trim();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Configuració del sistema</h1>
        <p className="text-muted-foreground mt-1">
          Paràmetres generals editables sense tocar el .env ni redesplegar
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Enviament de correus (SMTP)</span>
            <span className="text-xs font-normal text-muted-foreground">
              Font actual: {source === 'database' ? 'Base de dades' : "Variables d'entorn (.env)"}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Aquesta configuració s'utilitza per a l'enviament de sol·licituds d'oferta i comandes a proveïdors
            des del mòdul de Compres, i per a qualsevol altre enviament de correu del sistema.
          </p>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 text-red-500 rounded-lg border border-red-500/20 text-sm">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 text-green-600 rounded-lg border border-green-500/20 text-sm">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium mb-1">Host SMTP</label>
                <input
                  type="text"
                  required
                  placeholder="smtp.gmail.com"
                  value={formData.host}
                  onChange={(e) => setFormData((p) => ({ ...p, host: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Port</label>
                <input
                  type="number"
                  required
                  value={formData.port}
                  onChange={(e) => setFormData((p) => ({ ...p, port: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium mb-1">Usuari</label>
                <input
                  type="text"
                  required
                  value={formData.user}
                  onChange={(e) => setFormData((p) => ({ ...p, user: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">
                  Contrasenya {hasPassword && '(deixar en blanc per no canviar-la)'}
                </label>
                <input
                  type="password"
                  required={!hasPassword}
                  placeholder={hasPassword ? '••••••••' : ''}
                  value={formData.pass}
                  onChange={(e) => setFormData((p) => ({ ...p, pass: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Remitent (From)</label>
              <input
                type="email"
                required
                placeholder="noreply@empresa.com"
                value={formData.from}
                onChange={(e) => setFormData((p) => ({ ...p, from: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={formData.secure}
                onChange={(e) => setFormData((p) => ({ ...p, secure: e.target.checked }))}
              />
              Connexió segura (SSL/TLS — normalment per al port 465)
            </label>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Desant...' : 'Desar configuració'}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Bank Accounts Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Comptes bancaris</span>
            {!showBankForm && (
              <button
                type="button"
                onClick={() => { setShowBankForm(true); setEditingBankId(null); setBankForm(emptyBankForm); setBankError(null); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg hover:bg-muted transition-colors"
              >
                <Plus className="h-4 w-4" />
                Afegir compte
              </button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Comptes bancaris de la societat. L'IBAN marcat com a predeterminat s'usarà en les factures i comunicacions enviades als clients.
          </p>

          {/* List */}
          {bankAccounts.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">IBAN</th>
                    <th className="text-left px-4 py-2 font-medium">Banc</th>
                    <th className="text-left px-4 py-2 font-medium">Titular</th>
                    <th className="text-center px-4 py-2 font-medium w-20">Defecte</th>
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {bankAccounts.map((acc) => (
                    <tr key={acc.id} className="border-t hover:bg-muted/30">
                      <td className="px-4 py-2 font-mono">{formatIban(acc.iban)}</td>
                      <td className="px-4 py-2 text-muted-foreground">{acc.bankName || '—'}</td>
                      <td className="px-4 py-2 text-muted-foreground">{acc.accountHolder || '—'}</td>
                      <td className="px-4 py-2 text-center">
                        {acc.isDefault === 1 && <Star className="h-4 w-4 text-amber-500 inline fill-amber-500" />}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            type="button"
                            onClick={() => handleBankEdit(acc)}
                            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleBankDelete(acc.id)}
                            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-red-500"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            !showBankForm && (
              <p className="text-xs text-muted-foreground italic">Cap compte bancari configurat encara.</p>
            )
          )}

          {/* Add / Edit form */}
          {showBankForm && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
              <h4 className="text-sm font-medium">{editingBankId ? 'Editar compte' : 'Nou compte bancari'}</h4>
              {bankError && (
                <div className="flex items-center gap-2 p-2 bg-red-500/10 text-red-500 rounded text-sm border border-red-500/20">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  {bankError}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">IBAN *</label>
                  <input
                    type="text"
                    placeholder="ES00 0000 0000 0000 0000 0000"
                    value={bankForm.iban}
                    onChange={(e) => setBankForm((p) => ({ ...p, iban: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm font-mono uppercase"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Nom del banc</label>
                  <input
                    type="text"
                    placeholder="CaixaBank, Sabadell..."
                    value={bankForm.bankName}
                    onChange={(e) => setBankForm((p) => ({ ...p, bankName: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Titular del compte</label>
                  <input
                    type="text"
                    placeholder="Roig i Fills Associats S.L."
                    value={bankForm.accountHolder}
                    onChange={(e) => setBankForm((p) => ({ ...p, accountHolder: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={bankForm.isDefault}
                      onChange={(e) => setBankForm((p) => ({ ...p, isDefault: e.target.checked }))}
                    />
                    Compte predeterminat
                  </label>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={handleBankCancel}
                  className="px-4 py-1.5 text-sm border rounded-lg hover:bg-muted transition-colors"
                >
                  Cancel·lar
                </button>
                <button
                  type="button"
                  onClick={handleBankSave}
                  disabled={savingBank}
                  className="flex items-center gap-2 px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {savingBank ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  {savingBank ? 'Desant...' : 'Desar'}
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dropbox Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-blue-500" />
            Dropbox
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Connecta el ERP amb el compte de Dropbox per poder seleccionar carpetes des dels formularis d'elements, proveïdors i clients.
            Crea una app a{' '}
            <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              dropbox.com/developers/apps
            </a>{' '}
            i afegeix <code className="bg-muted px-1 rounded text-xs">gestio.equiro.com</code> a "Chooser / Saver domains".
          </p>

          {dropboxError && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 text-red-500 rounded-lg border border-red-500/20 text-sm">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {dropboxError}
            </div>
          )}
          {dropboxSuccess && (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 text-green-600 rounded-lg border border-green-500/20 text-sm">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              {dropboxSuccess}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium mb-1">App Key</label>
            <input
              type="text"
              placeholder="xxxxxxxxxxxxxxx"
              value={dropboxKeyInput}
              onChange={(e) => setDropboxKeyInput(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleDropboxSave}
              disabled={dropboxSaving}
              className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {dropboxSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {dropboxSaving ? 'Guardant...' : 'Guardar App Key'}
            </button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Provar configuració</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Envia un correu de prova amb la configuració SMTP desada.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="email"
              placeholder="destinatari@exemple.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1 px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="button"
              onClick={handleSendTest}
              disabled={sendingTest || !testEmail}
              className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-muted transition-colors disabled:opacity-60"
            >
              {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Enviar prova
            </button>
          </div>
          {testResult && (
            <p className={`text-xs ${testResult.ok ? 'text-green-600' : 'text-red-500'}`}>
              {testResult.message}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
